import { useState } from 'react';
import { toast } from 'sonner';
import { booksService } from '@/services/books.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * PublishingCard — the top card in the admin editor's right sidebar
 * (DESIGN.md Artboard 4 + CLIENT_PLAN Phase 7). Shows a two-option
 * segmented control that flips `books.status` between `'pending'` and
 * `'live'`.
 *
 * Persistence semantics:
 *   - Click → optimistic-free PATCH (`booksService.update`).
 *   - Success → parent's `onBookChange` is called with the server's fresh
 *     `BookView`; the segmented control re-renders from that canonical
 *     value rather than local UI state. Avoids drift if the server
 *     normalises the status differently from what we sent.
 *   - Failure → toast via the standard ApiError path; control stays on the
 *     original value because we never locally guessed.
 *
 * The right sidebar is narrow, so the card is intentionally compact: title,
 * helper copy, segmented control, and a one-line "Last updated" footer
 * using the locale date (relative time is owned by the breadcrumb bar).
 */
export interface PublishingCardProps {
  book: BookView;
  onBookChange: (book: BookView) => void;
  className?: string;
}

type PublishStatus = 'pending' | 'live';

const OPTIONS: ReadonlyArray<{ value: PublishStatus; label: string; hint: string }> = [
  { value: 'pending', label: 'Pending', hint: 'Hidden from readers' },
  { value: 'live', label: 'Live', hint: 'Visible in the catalog' },
] as const;

const HINTS: Record<PublishStatus, string> = {
  pending: 'Hidden from readers',
  live: 'Visible in the catalog',
};

export function PublishingCard({ book, onBookChange, className }: PublishingCardProps) {
  const [pending, setPending] = useState<PublishStatus | null>(null);
  // Treat any non-'live' status as 'pending' for UI purposes. The server's
  // CHECK constraint limits values to this pair today, but the BookView type
  // leaves `status` as `string` so we can't rely on exhaustive narrowing.
  const current: PublishStatus = book.status === 'live' ? 'live' : 'pending';

  const handleSelect = async (next: PublishStatus) => {
    if (next === current || pending) return;
    setPending(next);
    try {
      const updated = await booksService.update(book.id, { status: next });
      onBookChange(updated);
      toast.success(next === 'live' ? 'Book is now live.' : 'Book moved back to Pending.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update the status.');
    } finally {
      setPending(null);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm',
        className,
      )}
    >
      <div>
        <div className="or-kicker text-or-slate-500">Publishing</div>
        <h2 className="mt-1 font-display text-[16px] font-bold text-or-slate-900">
          {current === 'live' ? 'Live in the catalog' : 'Pending review'}
        </h2>
        <p className="mt-1 text-[12px] leading-relaxed text-or-slate-500">
          Controls whether this book appears in the reader-facing library.
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label="Publishing status"
        className="flex items-stretch rounded-full border border-or-slate-200 bg-or-slate-50 p-1"
      >
        {OPTIONS.map((option) => {
          const selected = option.value === current;
          const busy = pending === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={!!pending || selected}
              onClick={() => void handleSelect(option.value)}
              className={cn(
                'flex flex-1 items-center justify-center rounded-full px-3 py-1.5 text-[12px] font-semibold transition',
                selected
                  ? option.value === 'live'
                    ? 'bg-or-green-700 text-white shadow-sm'
                    : 'bg-white text-or-slate-900 shadow-sm'
                  : 'text-or-slate-500 hover:text-or-slate-700',
                !selected && pending ? 'opacity-60' : '',
                busy && 'opacity-60',
              )}
            >
              {busy ? 'Saving…' : option.label}
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-or-slate-500">
        {current === 'live'
          ? HINTS.live
          : `${HINTS.pending}. Move to Live once all metadata and labels look good.`}
      </p>
    </div>
  );
}
